import { create } from 'zustand';
import { iamApi } from '../services/iam.api';
import type { TemporaryPresetValue } from '@/auth/auth-types';

interface Role {
    _id: string;
    name: string;
    scope: string;
    isSystemRole: boolean;
    isDefault: boolean;
    description: string;
    permissions: string[];
}

interface Membership {
    roleId: string;
    roleName: string;
    roleScope: string;
    workspaceId: string | null;
    workspaceName: string | null;
    organizationId?: string;
}

interface Member {
    _id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    isOrgAdmin: boolean;
    memberships: Membership[];
}

interface Invite {
    _id: string;
    email: string;
    status: string;
    workspaceId?: string;
    roleId: { _id: string; name: string; scope: string } | string;
    invitedBy: { email: string; firstName?: string; lastName?: string } | string;
    createdAt: string;
    expiresAt: string;
}

interface PermissionRegistry {
    permissions: Record<string, { label: string; category: string }>;
    byCategory: Record<string, { key: string; label: string; category: string }[]>;
}

interface IamState {
    roles: Role[];
    members: Member[];
    invites: Invite[];
    permissionRegistry: PermissionRegistry | null;
    loading: boolean;
    error: string | null;

    fetchRoles: () => Promise<void>;
    fetchMembers: () => Promise<void>;
    fetchInvites: () => Promise<void>;
    fetchPermissionRegistry: () => Promise<void>;
    sendInvite: (data: { email?: string; roleId?: string; workspaceId?: string; inviteId?: string }) => Promise<unknown>;
    resendInvite: (id: string) => Promise<void>;
    revokeInvite: (id: string) => Promise<void>;
    setMemberWorkspaceRole: (memberId: string, data: { workspaceId: string; roleId: string }) => Promise<void>;
    removeMemberWorkspaceRole: (memberId: string, data: { workspaceId: string }) => Promise<void>;
    setMemberOrgAdmin: (
        memberId: string,
        data: { isOrgAdmin: boolean; fallbackWorkspaceRoles?: { workspaceId: string; roleId: string }[] },
    ) => Promise<void>;
    addMemberRole: (memberId: string, data: { roleId: string; workspaceId?: string }) => Promise<void>;
    removeMemberRole: (memberId: string, data: { roleId: string; workspaceId?: string }) => Promise<void>;
    removeMember: (memberId: string) => Promise<void>;
    createOrg: (data: {
        name: string;
        domain: string;
        description?: string;
        website?: string;
        temporaryPreset?: TemporaryPresetValue;
    }) => Promise<unknown>;
}

const useIamStore = create<IamState>((set, get) => ({
    roles: [],
    members: [],
    invites: [],
    permissionRegistry: null,
    loading: false,
    error: null,

    fetchRoles: async () => {
        set({ loading: true, error: null });
        try {
            const { data } = await iamApi.listRoles();
            set({ roles: data, loading: false });
        } catch (e: any) {
            set({ error: e.message, loading: false });
        }
    },

    fetchMembers: async () => {
        set({ loading: true, error: null });
        try {
            const { data } = await iamApi.listMembers();
            set({ members: data, loading: false });
        } catch (e: any) {
            set({ error: e.message, loading: false });
        }
    },

    fetchInvites: async () => {
        set({ loading: true, error: null });
        try {
            const { data } = await iamApi.listInvites();
            set({ invites: data, loading: false });
        } catch (e: any) {
            set({ error: e.message, loading: false });
        }
    },

    fetchPermissionRegistry: async () => {
        try {
            const { data } = await iamApi.getPermissions();
            set({ permissionRegistry: data });
        } catch (e: any) {
            set({ error: e.message });
        }
    },

    sendInvite: async (data) => {
        set({ loading: true, error: null });
        try {
            const result = await iamApi.sendInvite(data);
            set({ loading: false });
            get().fetchInvites();
            return result.data;
        } catch (e: any) {
            set({ error: e.response?.data?.error || e.message, loading: false });
            throw e;
        }
    },

    resendInvite: async (id) => {
        try {
            await iamApi.resendInvite(id);
            get().fetchInvites();
        } catch (e: any) {
            set({ error: e.response?.data?.error || e.message });
            throw e;
        }
    },

    revokeInvite: async (id) => {
        try {
            await iamApi.revokeInvite(id);
            get().fetchInvites();
        } catch (e: any) {
            set({ error: e.message });
        }
    },

    setMemberWorkspaceRole: async (memberId, data) => {
        try {
            await iamApi.setMemberWorkspaceRole(memberId, data);
            get().fetchMembers();
        } catch (e: any) {
            set({ error: e.response?.data?.error || e.message });
            throw e;
        }
    },

    removeMemberWorkspaceRole: async (memberId, data) => {
        try {
            await iamApi.removeMemberWorkspaceRole(memberId, data);
            get().fetchMembers();
        } catch (e: any) {
            set({ error: e.response?.data?.error || e.message });
            throw e;
        }
    },

    setMemberOrgAdmin: async (memberId, data) => {
        try {
            await iamApi.setMemberOrgAdmin(memberId, data);
            get().fetchMembers();
        } catch (e: any) {
            set({ error: e.response?.data?.error || e.message });
            throw e;
        }
    },

    addMemberRole: async (memberId, data) => {
        try {
            await iamApi.addMemberRole(memberId, data);
            get().fetchMembers();
        } catch (e: any) {
            set({ error: e.response?.data?.error || e.message });
            throw e;
        }
    },

    removeMemberRole: async (memberId, data) => {
        try {
            await iamApi.removeMemberRole(memberId, data);
            get().fetchMembers();
        } catch (e: any) {
            set({ error: e.response?.data?.error || e.message });
            throw e;
        }
    },

    removeMember: async (memberId) => {
        try {
            await iamApi.removeMember(memberId);
            get().fetchMembers();
        } catch (e: any) {
            set({ error: e.message });
        }
    },

    createOrg: async (data) => {
        set({ loading: true, error: null });
        try {
            const result = await iamApi.createOrg(data);
            set({ loading: false });
            return result.data;
        } catch (e: any) {
            set({ error: e.response?.data?.error || e.message, loading: false });
            throw e;
        }
    },
}));

export default useIamStore;
